import React, { useMemo } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ChartBar, Note, PencilSimpleLine, UserSquare } from '@phosphor-icons/react'
import FormHeaderDetailActions from '../../../components/action_button/FormHeaderDetailActions'
import {
  groupScopesForModule,
  groupHasPermissions,
  rolePermissionsToMap
} from '../../utils/permissions'
import RbacRoleSectionCard from '../components/RbacRoleSectionCard'
import { RBAC_ROLE_DETAIL_TEXTS } from '../shared/rbacRoleDetailConstants'
import { RolePermissionPresetActions } from '../RoleWizard/RoleWizardPermissionsStep'
import {
  permissionGroupPanelSx,
  permissionReadOnlySpecialScopeSx,
  permissionScopeDescriptionCompactSx,
  permissionScopeGridSx,
  permissionScopeTitleCompactSx,
  permissionSpecialBadgeSx,
  scopeDescription,
  scopeStateKey,
  scopeTitle
} from '../RoleWizard/roleWizardConstants'

const MODULE_SECTION_ICONS = {
  mide: ChartBar,
  planifica: Note,
  ejecuta: UserSquare
}

function PermissionScopeSummary({ moduleKey, scope, actions, special = false }) {
  return (
    <Box
      sx={{
        ...permissionScopeGridSx,
        px: special ? { xs: 1.75, md: 2 } : { xs: 2, md: 2.25 },
        py: special ? 1.55 : { xs: 1.75, md: 2 },
        ...(special ? permissionReadOnlySpecialScopeSx : {})
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={permissionScopeTitleCompactSx}>
          {scopeTitle(moduleKey, scope)}
        </Typography>
        <Typography sx={permissionScopeDescriptionCompactSx}>
          {scopeDescription(moduleKey, scope)}
        </Typography>
      </Box>
      <RolePermissionPresetActions
        scope={scope}
        selectedActions={actions}
        compact={special}
        readOnly
        selectedOnly
      />
    </Box>
  )
}

function SpecialPermissionsLabel() {
  return (
    <Typography component="span" sx={permissionSpecialBadgeSx}>
      Configuraciones especiales
    </Typography>
  )
}

function PermissionGroupSummary({ moduleKey, group, permissionsByScope }) {
  const parentActions = group.parentScope
    ? permissionsByScope[scopeStateKey(moduleKey, group.parentScope.key)] || []
    : []
  const visibleChildren = (group.children || [])
    .map((scope) => ({
      scope,
      actions: permissionsByScope[scopeStateKey(moduleKey, scope.key)] || []
    }))
    .filter(({ actions }) => actions.length > 0)

  if (parentActions.length === 0 && visibleChildren.length === 0) return null

  return (
    <Box sx={permissionGroupPanelSx}>
      {group.parentScope && parentActions.length > 0 && (
        <PermissionScopeSummary
          moduleKey={moduleKey}
          scope={group.parentScope}
          actions={parentActions}
        />
      )}

      {visibleChildren.length > 0 && (
        <Box sx={{ px: { xs: 2, md: 2.25 }, pb: 2, pt: parentActions.length > 0 ? 0 : 2 }}>
          <SpecialPermissionsLabel />
          <Stack spacing={1.25}>
            {visibleChildren.map(({ scope, actions }) => (
              <PermissionScopeSummary
                key={scope.key}
                moduleKey={moduleKey}
                scope={scope}
                actions={actions}
                special
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default function RbacRolePermissionsTab({ role, catalog, canManage = false, onEditRole }) {
  const permissionsByScope = useMemo(() => rolePermissionsToMap(role?.permissions), [role])

  const modulesWithPermissions = useMemo(() => {
    if (!catalog?.modules || !role?.permissions?.length) return []

    const moduleKeys = [...new Set(role.permissions.map((permission) => permission.module_key))]
    return catalog.modules.filter((module) => moduleKeys.includes(module.key))
  }, [catalog, role])

  return (
    <div className="container-fluid px-0">
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mt: 3,
          mb: 2
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, m: 0, lineHeight: 1.3 }}>
            {RBAC_ROLE_DETAIL_TEXTS.permissionsTitle}
          </Typography>
        </Box>

        {canManage ? (
          <FormHeaderDetailActions
            showSecondary={false}
            primaryLabel={RBAC_ROLE_DETAIL_TEXTS.editRole}
            primaryIcon={PencilSimpleLine}
            onPrimary={onEditRole}
            stackOnMobile
          />
        ) : null}
      </Box>

      {modulesWithPermissions.length === 0 && (
        <Typography color="text.secondary">Este rol no tiene permisos asignados.</Typography>
      )}

      <Stack spacing={2}>
        {modulesWithPermissions.map((mod) => {
          const groups = groupScopesForModule(mod).filter((group) =>
            groupHasPermissions(group, mod.key, permissionsByScope)
          )

          if (groups.length === 0) return null

          return (
            <RbacRoleSectionCard
              key={mod.key}
              title={`Permisos de ${mod.label}`}
              icon={MODULE_SECTION_ICONS[mod.key]}
              iconName="fact_check"
              bodySx={{ p: { xs: 2, md: 2.5 } }}
            >
              <Stack spacing={1.5}>
                {groups.map((group) => (
                  <PermissionGroupSummary
                    key={group.parentScope?.key || group.key}
                    moduleKey={mod.key}
                    group={group}
                    permissionsByScope={permissionsByScope}
                  />
                ))}
              </Stack>
            </RbacRoleSectionCard>
          )
        })}
      </Stack>
    </div>
  )
}
